/**
 * Hook que detecta si el usuario actual tiene una inscripción a un evento
 * cuyo consentimiento (check-in de documentos) está pendiente.
 *
 * Uso:
 *   const { required, registration, member, person, consent, loading } =
 *     useEventConsentRequired(eventId);
 *
 * Si `required === true`, se debe mostrar el `<EventConsentGate>` o el modal
 * directamente.
 */

import { useEffect, useState } from "react";
import { useAuthStore } from "@/stores/authStore";
import { eventService } from "@/lib/services/events";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "@/lib/firebase";
import {
  getConsentById,
  isConsentCompleted,
} from "@/lib/services/eventConsents";
import type {
  Event,
  EventRegistration,
  EventConsent,
  Member,
  Person,
} from "@/types";

export interface UseEventConsentRequiredResult {
  loading: boolean;
  /** El consentimiento es necesario y no está completo */
  required: boolean;
  event: Event | null;
  registration: EventRegistration | null;
  member: Member | null;
  person: Person | null;
  consent: EventConsent | null;
  refresh: () => Promise<void>;
}

export function useEventConsentRequired(
  eventId: string | undefined,
): UseEventConsentRequiredResult {
  const { profile } = useAuthStore();
  const [loading, setLoading] = useState<boolean>(false);
  const [event, setEvent] = useState<Event | null>(null);
  const [registration, setRegistration] = useState<EventRegistration | null>(
    null,
  );
  const [member, setMember] = useState<Member | null>(null);
  const [person, setPerson] = useState<Person | null>(null);
  const [consent, setConsent] = useState<EventConsent | null>(null);

  const load = async () => {
    if (!eventId || !profile?.personId) {
      setRegistration(null);
      setConsent(null);
      return;
    }
    setLoading(true);
    try {
      // Buscar la inscripción del usuario actual a este evento
      const regsSnap = await getDocs(
        query(
          collection(db, "eventRegistrations"),
          where("eventId", "==", eventId),
          where("personId", "==", profile.personId),
        ),
      );
      const regDoc = regsSnap.docs[0];
      if (!regDoc) {
        setRegistration(null);
        setConsent(null);
        setLoading(false);
        return;
      }
      const reg = { id: regDoc.id, ...regDoc.data() } as EventRegistration;
      setRegistration(reg);

      // Cargar evento, member, person, consent en paralelo
      const [evt, memberDoc, personDoc] = await Promise.all([
        eventService.getById(eventId),
        reg.memberId
          ? (
              await getDocs(
                query(
                  collection(db, "members"),
                  where("__name__", "==", reg.memberId),
                ),
              )
            ).docs[0]
          : null,
        (
          await getDocs(
            query(
              collection(db, "persons"),
              where("__name__", "==", reg.personId),
            ),
          )
        ).docs[0],
      ]);

      setEvent(evt);
      setMember(
        memberDoc
          ? ({ id: memberDoc.id, ...memberDoc.data() } as Member)
          : null,
      );
      setPerson(
        personDoc
          ? ({ id: personDoc.id, ...personDoc.data() } as Person)
          : null,
      );

      const memberId = reg.memberId;
      if (memberId) {
        const c = await getConsentById(eventId, memberId);
        setConsent(c);
      }
    } catch (err) {
      console.error("[useEventConsentRequired] error", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, profile?.personId]);

  const required =
    !!event &&
    !!registration &&
    !!member &&
    !!person &&
    registration.status !== "cancelled" &&
    registration.status !== "rejected" &&
    !isConsentCompleted(consent, event.consentChecklistConfig);

  return {
    loading,
    required,
    event,
    registration,
    member,
    person,
    consent,
    refresh: load,
  };
}
